import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Input,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaShieldAlt, FaFileDownload } from "react-icons/fa";
import moment from "moment";
import {
  ivApi,
  isOk,
  saveBlobUrl,
  formatDay,
  formatDate,
  money,
  humanize,
  networkLabel,
  percentLabel,
  mismatchWarnings,
  procedureRows,
  filterProcedures,
  answersFirst,
  pmsState,
  errorMessage,
  pendingLabel,
  settlingAfterDone,
  SETTLE_WINDOW_MS,
} from "./insuranceVerification";

/**
 * EligibilityDetailsDrawer — one verification's result (SS-3944).
 *
 * When the practice has questions on, a done result opens on "Your questions" (each
 * answer with its source chip, "Not in the insurer's data" when nothing was found);
 * "Show full breakdown" opens the full result and the procedure table below it. With no
 * questions, the full result shows directly. Download offers Answers (the default when
 * questions exist) and Full; the PMS state shows where the PMS takes files.
 */

const Section = ({ title, children, subColor, testId }) => (
  <Box mb="5" data-testid={testId}>
    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color={subColor} mb="2">{title}</Text>
    {children}
  </Box>
);

const Row = ({ label, value, labelColor, subColor }) => (
  <Flex justify="space-between" py="1" gap="3">
    <Text fontSize="13px" color={subColor}>{label}</Text>
    <Text fontSize="13px" fontWeight="600" color={labelColor} textAlign="right">{value}</Text>
  </Flex>
);

const amountText = (a) => {
  if (a.unlimited) return "Unlimited";
  const total = money(a.amount);
  if (a.remaining === undefined || a.remaining === null) return total || "—";
  return total ? `${money(a.remaining)} left of ${total}` : `${money(a.remaining)} left`;
};

/** A waiting period's detail: a plain value, or the payer's structured period ({ value, unit, ... }). */
export const waitingDetail = (d) => {
  if (d === undefined || d === null || d === "") return "";
  if (typeof d !== "object") return ` · ${d}`;
  const parts = Object.entries(d)
    .filter(([k, x]) => k !== "category" && x !== null && x !== undefined && typeof x !== "object")
    .map(([k, x]) => `${humanize(k)} ${humanize(String(x))}`);
  return parts.length ? ` · ${parts.join(", ")}` : "";
};

/** Times a code was used in the current benefit period, from the payer's history. */
/**
 * Where a frequency's window starts: "1 per 5 Years" looks back 5 years, "2 per 1 CalendarYear"
 * to the start of this calendar (benefit) year; with no period, the benefit year.
 */
export const usageSince = (period, benefitStart, now = new Date()) => {
  const value = Number(period?.value) || 1;
  const unit = String(period?.unit || "");
  const n = moment.utc(now);
  // a calendar year starts on January 1, whatever the plan's benefit year
  if (/^calendaryear/i.test(unit)) return n.clone().startOf("year").subtract(value - 1, "years").toDate();
  if (/^benefityear|^contractyear|^planyear/i.test(unit)) {
    const yearStart = benefitStart ? moment.utc(benefitStart) : n.clone().startOf("year");
    return yearStart.subtract(value - 1, "years").toDate();
  }
  if (/^year/i.test(unit)) return n.subtract(value, "years").toDate();
  if (/^month/i.test(unit)) return n.subtract(value, "months").toDate();
  if (/^week/i.test(unit)) return n.subtract(value, "weeks").toDate();
  if (/^day/i.test(unit)) return n.subtract(value, "days").toDate();
  if (/^lifetime/i.test(unit)) return null; // every recorded service counts
  if (!unit) return benefitStart ? new Date(benefitStart) : null;
  // per visit, per tooth…: the payer's history cannot say which services shared it — no count
  return undefined;
};

/** Times a code was used since `since`, from the payer's history. */
export const timesUsed = (raw, code, since, until) => {
  let n = 0;
  for (const h of raw?.subscriber?.history || []) {
    if (!(h.codes || []).includes(code)) continue;
    const d = h.date && typeof h.date === "object" ? (h.date.final ?? h.date.original) : h.date;
    const at = d ? new Date(d) : null;
    if (since && !(at && at >= since)) continue;
    // nothing after the check's date of service counts against it
    if (until && at && at > until) continue;
    n += 1;
  }
  return n;
};

export function YourQuestions({ v, subColor, labelColor, onShowFull, onReanswer, reanswering, canReanswer }) {
  const items = v?.answers?.items || [];
  return (
    <Box mb="5" data-testid="iv-your-questions">
      <Flex justify="space-between" align="center" mb="2">
        <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color={subColor}>Your questions</Text>
        {canReanswer && (
          <Button size="xs" variant="outline" onClick={onReanswer} isLoading={reanswering} data-testid="iv-reanswer">
            Re-answer with current questions
          </Button>
        )}
      </Flex>
      {v?.answers?.status === "pending" && (
        <Flex align="center" gap="2"><Spinner size="xs" /><Text fontSize="13px" color={subColor}>Answering your questions…</Text></Flex>
      )}
      {v?.answers?.status === "failed" && (
        <Text fontSize="13px" color="#8A5A00">Your questions could not be answered this time. The full result is below.</Text>
      )}
      {items.map((it) => (
        <Box key={it.question_id} py="2" borderBottom="1px solid" borderColor="#E5E5EF" data-testid="iv-answer">
          <Text fontSize="13px" fontWeight="600" color={labelColor}>{it.question}</Text>
          {it.status === "not_found" ? (
            <Text fontSize="13px" color={subColor} fontStyle="italic">{"Not in the insurer's data"}</Text>
          ) : (
            <Text fontSize="13px" color={labelColor}>{it.answer}{it.status === "conflicting" ? " (the insurer's data conflicts)" : ""}</Text>
          )}
          {it.status !== "not_found" && it.source && (
            <Flex gap="1" wrap="wrap" mt="1">
              {String(it.source).split("; ").map((s) => (
                <Badge key={s} variant="subtle" colorScheme="purple" borderRadius="full" px="2" fontSize="10px" textTransform="none" data-testid="iv-source-chip">{s}</Badge>
              ))}
            </Flex>
          )}
        </Box>
      ))}
      {onShowFull && (
        <Button size="sm" variant="link" color="#8E4997" mt="2" onClick={onShowFull} data-testid="iv-show-full">Show full breakdown</Button>
      )}
    </Box>
  );
}

export function FullResult({ v, subColor, labelColor, dividerColor, sectionBg }) {
  const [search, setSearch] = useState("");
  const s = v?.summary || {};
  const raw = v?.raw_result;
  const rows = useMemo(() => procedureRows(raw), [raw]);
  const shown = filterProcedures(rows, search);
  const bp = s.benefit_period || {};
  const asOfRaw = v?.request?.appointment?.serviceDate || v?.completed || v?.created;
  const asOf = asOfRaw ? new Date(asOfRaw) : new Date();
  const warnings = mismatchWarnings(v);
  const clauses = [
    s.missing_tooth_clause !== null && s.missing_tooth_clause !== undefined
      ? `Missing-tooth clause: ${s.missing_tooth_clause === true ? "Yes" : s.missing_tooth_clause === false ? "No" : String(s.missing_tooth_clause)}` : null,
    s.coordination_of_benefits
      ? `Coordination of benefits: ${typeof s.coordination_of_benefits === "object" ? humanize(s.coordination_of_benefits.paymentOptionType || "") : String(s.coordination_of_benefits)}` : null,
  ].filter(Boolean);

  return (
    <Box data-testid="iv-full-result">
      {warnings.length > 0 && (
        <Alert status="warning" borderRadius="8px" mb="4" fontSize="13px" alignItems="flex-start" data-testid="iv-mismatches">
          <AlertIcon />
          <Box>{warnings.map((w) => <Text key={w}>{w}</Text>)}</Box>
        </Alert>
      )}
      <Section title="Plan" subColor={subColor} testId="iv-plan">
        <Row label="Plan" value={s.plan_name || "—"} labelColor={labelColor} subColor={subColor} />
        <Row label="Type" value={s.plan_type || "—"} labelColor={labelColor} subColor={subColor} />
        <Row label="Network" value={s.network || "—"} labelColor={labelColor} subColor={subColor} />
        <Row label="Benefit year" value={bp.start || bp.end ? `${humanize(bp.type) || ""} ${formatDay(bp.start)} – ${formatDay(bp.end)}`.trim() : (humanize(bp.type) || "—")} labelColor={labelColor} subColor={subColor} />
        {s.group?.id && <Row label="Group" value={s.group.id} labelColor={labelColor} subColor={subColor} />}
      </Section>

      {(s.deductibles || []).length > 0 && (
        <Section title="Deductibles" subColor={subColor} testId="iv-deductibles">
          {s.deductibles.map((d, i) => (
            <Row key={`d${i}`} label={`${d.coverage_level || "—"} · ${networkLabel(d.network) || "any network"} · ${humanize(d.period)}`} value={amountText(d)} labelColor={labelColor} subColor={subColor} />
          ))}
        </Section>
      )}
      {(s.maximums || []).length > 0 && (
        <Section title="Maximums" subColor={subColor} testId="iv-maximums">
          {s.maximums.map((m, i) => (
            <Row key={`m${i}`} label={`${m.coverage_level || "—"} · ${networkLabel(m.network) || "any network"} · ${humanize(m.period)}${m.categories?.length === 1 ? ` · ${humanize(m.categories[0])}` : ""}`} value={amountText(m)} labelColor={labelColor} subColor={subColor} />
          ))}
        </Section>
      )}
      {(s.coinsurance || []).length > 0 && (
        <Section title="Coverage by category (plan pays)" subColor={subColor} testId="iv-coverage">
          {s.coinsurance.map((c, i) => (
            <Row key={`c${i}`} label={`${humanize(c.category)} · ${networkLabel(c.network)}`} value={percentLabel(c.min, c.max)} labelColor={labelColor} subColor={subColor} />
          ))}
        </Section>
      )}
      {(s.frequencies || []).length > 0 && (
        <Section title="Frequencies" subColor={subColor} testId="iv-frequencies">
          {s.frequencies.map((f, i) => {
            // counted as of the check's date of service, so a saved result reads the same any day
            const since = usageSince(f.period, bp.start, asOf);
            const used = since === undefined ? null : timesUsed(raw, f.code, since, moment.utc(asOf).endOf("day").toDate());
            return (
              <Row key={`f${i}`} label={`${f.code} · ${humanize(f.category)}`}
                value={`${humanize(f.frequency)}${raw && used !== null ? ` · ${used} used` : ""}${f.last_service_date ? ` · last ${formatDay(f.last_service_date)}` : ""}`}
                labelColor={labelColor} subColor={subColor} />
            );
          })}
        </Section>
      )}
      <Section title="Waiting periods and clauses" subColor={subColor} testId="iv-clauses">
        {(s.waiting_periods || []).length === 0 && <Text fontSize="13px" color={subColor}>{"No waiting periods in the insurer's data"}</Text>}
        {(s.waiting_periods || []).map((w, i) => (
          <Text key={`w${i}`} fontSize="13px" color={labelColor}>{[w.code, humanize(w.category)].filter(Boolean).join(" · ")}{waitingDetail(w.detail)}</Text>
        ))}
        {clauses.map((c) => <Text key={c} fontSize="13px" color={labelColor}>{c}</Text>)}
      </Section>

      {rows.length > 0 && (
        <Section title="Procedure codes" subColor={subColor} testId="iv-procedures">
          <Input size="sm" borderRadius="8px" placeholder="Search a code (e.g. D1110) or category" mb="2"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="iv-procedure-search" />
          <Box border="1px solid" borderColor={dividerColor} borderRadius="8px" overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg={sectionBg}>
                <Tr><Th>Code</Th><Th>Coverage</Th><Th>Frequency</Th><Th>Age</Th><Th>Pre-auth</Th><Th>Last done</Th></Tr>
              </Thead>
              <Tbody>
                {shown.map((r) => (
                  <Tr key={`${r.code}|${r.network}`} data-testid="iv-procedure-row">
                    <Td fontWeight="600">
                      <Text fontSize="12px">{r.code}</Text>
                      <Text fontSize="11px" color={subColor}>{r.category}{r.network ? ` · ${r.network}` : ""}</Text>
                    </Td>
                    <Td fontSize="12px">{r.coverage || "—"}</Td>
                    <Td fontSize="12px">{r.frequency || "—"}</Td>
                    <Td fontSize="12px">{r.age || "—"}</Td>
                    <Td fontSize="12px">{r.preauth || "—"}</Td>
                    <Td fontSize="12px">{r.last || "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          {shown.length === 0 && <Text fontSize="12px" color={subColor} mt="2">No procedure matches “{search}”.</Text>}
        </Section>
      )}
    </Box>
  );
}

export function DownloadPdf({ v, hasQuestions, size = "sm" }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const download = async (variant) => {
    setBusy(true);
    setFailed(false);
    try {
      const url = await ivApi.downloadPdf(v._id, variant);
      if (!saveBlobUrl(url, `insurance-verification-${v._id}-${variant}.pdf`)) setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  if (!v || v.status !== "done") return null;
  const hasAnswers = hasQuestions && v.answers && v.answers.status !== "none";
  return (
    <Flex align="center" gap="2">
      {hasAnswers ? (
        <Menu>
          <MenuButton as={Button} size={size} variant="outline" borderRadius="8px" leftIcon={<FaFileDownload />} isLoading={busy} data-testid="iv-download">
            Download PDF
          </MenuButton>
          <MenuList fontSize="13px">
            <MenuItem onClick={() => download("answers")} data-testid="iv-download-answers">Answers (default)</MenuItem>
            <MenuItem onClick={() => download("full")} data-testid="iv-download-full">Full</MenuItem>
          </MenuList>
        </Menu>
      ) : (
        <Button size={size} variant="outline" borderRadius="8px" leftIcon={<FaFileDownload />} isLoading={busy}
          onClick={() => download("full")} data-testid="iv-download">
          Download PDF
        </Button>
      )}
      {failed && <Text fontSize="12px" color="#B3261E">Download failed</Text>}
    </Flex>
  );
}

export function PmsStateLine({ v, onRetried }) {
  const [busy, setBusy] = useState(false);
  const state = pmsState(v);
  if (!state) return null;
  const retry = async () => {
    setBusy(true);
    try {
      const r = await ivApi.retryPmsWrite(v._id);
      if (isOk(r)) onRetried && onRetried(r.data);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Flex align="center" gap="2" data-testid="iv-pms-state">
      <Text fontSize="12px" fontWeight="600" color={state.kind === "written" ? "#1F7A4D" : state.kind === "failed" ? "#B3261E" : "#64728C"}>{state.label}</Text>
      {state.kind === "failed" && (
        <Button size="xs" variant="link" color="#8E4997" onClick={retry} isLoading={busy} data-testid="iv-pms-retry">· Retry</Button>
      )}
    </Flex>
  );
}

// the same questions in the same order (staff reordering them is a change too)
export const sameQuestions = (snapshot, questions) => {
  const ordered = (list) => [...(list || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((q) => `${q.id}|${q.text}`);
  const now = ordered((questions || []).filter((q) => q && q.enabled !== false));
  const then = ordered(snapshot);
  return now.length === then.length && now.every((x, i) => x === then[i]);
};

// a read the card asked for (the check finished, its answers or PMS save came in) that fails is tried again
const READ_RETRIES = 3;
const READ_RETRY_MS = 5000;

// payerNames: { [commonPayerId]: name } — the insurer as the card that opened the result names it (the
// patient's own insurer, SS-4455); a result of another payer keeps the name the API stored
export default function EligibilityDetailsDrawer({ isOpen, onClose, verificationId, settings, onChanged, onRecheck, statusKey, payerNames }) {
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [reanswering, setReanswering] = useState(false);
  // bumped when this drawer re-answers: a read that started before never brings the old answers back
  const answersReplaced = useRef(0);
  const [, setWindowOver] = useState(0);

  const labelColor = useColorModeValue("#11131B", "#F9F9FB");
  const subColor = useColorModeValue("#64728C", "#A9B1C2");
  const sectionBg = useColorModeValue("#F9F9FB", "#2A2D3A");
  const dividerColor = useColorModeValue("#E5E5EF", "#3A3D4D");

  // another result opens on its own default view; the same one reloading (its answers or PMS save
  // came in) keeps the view staff chose
  useEffect(() => {
    setShowFull(false);
  }, [isOpen, verificationId]);

  useEffect(() => {
    if (!isOpen || !verificationId) return undefined;
    let live = true;
    let retry = null;
    const read = async (attempt) => {
      const replaced = answersReplaced.current;
      let r = null;
      try {
        r = await ivApi.getVerification(verificationId, { raw: true });
      } catch (e) {
        r = null;
      }
      if (!live) return;
      if (isOk(r)) {
        setV((cur) => (replaced !== answersReplaced.current && cur?._id === r.data?._id ? { ...r.data, answers: cur.answers } : r.data));
      } else if (attempt < READ_RETRIES) {
        retry = setTimeout(() => read(attempt + 1), READ_RETRY_MS);
        return;
      }
      setLoading(false);
    };
    setLoading(true);
    read(0);
    return () => {
      live = false;
      clearTimeout(retry);
    };
  }, [isOpen, verificationId, statusKey]); // statusKey: the card saw this check change (it finished, its answers or PMS save came in)

  // answers still being written when their window ends are stuck: the open drawer offers Re-answer then
  const answersSettling = v?.answers?.status === "pending" && settlingAfterDone(v);
  const finishedAt = v ? new Date(v.completed || v.created || NaN).getTime() : NaN;
  useEffect(() => {
    if (!answersSettling || !Number.isFinite(finishedAt)) return undefined;
    const t = setTimeout(() => setWindowOver((n) => n + 1), Math.max(0, finishedAt + SETTLE_WINDOW_MS - Date.now()) + 1000);
    return () => clearTimeout(t);
  }, [answersSettling, finishedAt]);

  const questionsOn = (settings?.questions || []).some((q) => q && q.enabled !== false);
  const leadWithAnswers = answersFirst(v, settings) && !showFull;
  // answers being written right now use today's questions already: nothing to re-answer yet (answers
  // still pending long after the check are stuck, and can be re-answered)
  const canReanswer = questionsOn && v?.status === "done" && !answersSettling
    && (v?.answers?.status === "failed" || !sameQuestions(v?.answers?.question_snapshot, settings?.questions));

  const reanswer = async () => {
    setReanswering(true);
    try {
      const r = await ivApi.reanswer(v._id);
      if (isOk(r)) {
        answersReplaced.current += 1;
        setV((cur) => ({ ...cur, answers: r.data }));
        onChanged && onChanged();
      }
    } finally {
      setReanswering(false);
    }
  };

  const coverage = v?.summary?.coverage_status || "";
  const active = coverage === "Active";
  const err = errorMessage(v);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={{ base: "full", md: "md" }}>
      <DrawerOverlay />
      <DrawerContent data-testid="iv-drawer">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={dividerColor}>
          <Flex align="center" gap="8px" wrap="wrap">
            <Box color={active ? "#154043" : "#B3261E"}><FaShieldAlt /></Box>
            <Text fontSize="16px" fontWeight="700" color={labelColor}>Eligibility result</Text>
            {v?.status === "done" && (
              <Badge colorScheme={active ? "green" : coverage ? "red" : "gray"} borderRadius="full" px="2" data-testid="iv-coverage-badge">
                {active ? "Active" : coverage ? "Inactive" : "Status not reported"}
              </Badge>
            )}
          </Flex>
          {v && (
            <Text fontSize="12px" fontWeight="400" color={subColor} mt="1">
              {(v.payer?.common_payer_id && payerNames?.[v.payer.common_payer_id]) || v.payer?.name}{v.completed ? ` · ${formatDate(v.completed)}` : ""}{v.type === "breakdown" ? " · Full breakdown" : ""}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody pt="4">
          {loading && !v && <Flex justify="center" py="10"><Spinner /></Flex>}
          {v && ["requested", "in_progress", "retrying"].includes(v.status) && (
            <Flex align="center" gap="2" py="4" data-testid="iv-drawer-pending">
              <Spinner size="sm" />
              <Text fontSize="13px" color={subColor}>{pendingLabel(v)}</Text>
            </Flex>
          )}
          {err && (
            <Alert status={err.kind === "unavailable" ? "info" : "error"} borderRadius="8px" mb="4" fontSize="13px" alignItems="flex-start">
              <AlertIcon />
              <Box>
                <Text fontWeight="700">{err.title}</Text>
                <Text>{err.text}</Text>
                <Text fontSize="12px" mt="1">{err.charged ? "This check was charged." : "You were not charged."}</Text>
              </Box>
            </Alert>
          )}
          {v?.status === "done" && leadWithAnswers && (
            <YourQuestions v={v} subColor={subColor} labelColor={labelColor} onShowFull={() => setShowFull(true)}
              onReanswer={reanswer} reanswering={reanswering} canReanswer={canReanswer} />
          )}
          {v?.status === "done" && !leadWithAnswers && (
            <>
              {questionsOn && v.answers && v.answers.status !== "none" && (
                <YourQuestions v={v} subColor={subColor} labelColor={labelColor}
                  onReanswer={reanswer} reanswering={reanswering} canReanswer={canReanswer} />
              )}
              <FullResult v={v} subColor={subColor} labelColor={labelColor} dividerColor={dividerColor} sectionBg={sectionBg} />
            </>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={dividerColor} gap="3" flexWrap="wrap" justifyContent="space-between">
          {v && <PmsStateLine v={v} onRetried={(data) => { setV((cur) => ({ ...cur, pms_write: { ...(cur.pms_write || {}), ...data } })); onChanged && onChanged(); }} />}
          <Flex gap="2" ml="auto">
            {v && <DownloadPdf v={v} hasQuestions={questionsOn} />}
            {onRecheck && v?.status === "failed" && (
              <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} borderRadius="8px" onClick={() => onRecheck(v.type)}>Edit &amp; retry</Button>
            )}
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
